/**
 * Typed wrappers around every RPC the apps call.
 *
 * Each of these corresponds to one function in `supabase/migrations`. The
 * generated `Database` types describe the return as `Json`, so the casts here
 * are where the server's `jsonb_build_object` payload is bound to the interfaces
 * in `@yummy/shared`. Keeping that binding in one file means a change to a
 * database payload surfaces as a small, obvious edit rather than a scattering of
 * casts across screens.
 */

import type {
  CartLine,
  CookInsights,
  CookQueue,
  EmailStatus,
  DashboardWindow,
  DashboardStats,
  EtaInsights,
  MerchantMenu,
  MerchantRestaurant,
  Order,
  OrderQuote,
  PublicMenu,
  RestaurantPublic,
  RestaurantSummary,
  RestaurantTable,
  ScanResolution,
} from '@yummy/shared';

import { unwrap, type DbClient } from './client';
import type { Json } from './database.types';

/** Cart payload accepted by `quote_order` and `place_order`. */
export interface CartItemInput {
  item_id: string;
  quantity: number;
}

/**
 * Widens a value to the generated `Json` type for an RPC argument.
 *
 * The generator types every `jsonb` parameter as `Json`, which a plain
 * `CartItemInput[]` satisfies at runtime but not structurally — `Json` includes
 * an index signature the interface does not declare. Narrowing at the boundary
 * keeps the cast in one place instead of at every call site.
 *
 * @param value A JSON-serialisable argument.
 * @returns The same value typed as `Json`.
 */
function asJson(value: unknown): Json {
  return value as Json;
}

/**
 * Converts local cart lines into the server's expected payload.
 *
 * Only the item id and quantity travel; prices are the server's business.
 *
 * @param lines Cart lines held on the device.
 * @returns The JSON array to send.
 */
export function toCartPayload(lines: CartLine[]): CartItemInput[] {
  return lines.map((line) => ({ item_id: line.itemId, quantity: line.quantity }));
}

// ---------------------------------------------------------------------------
// Customer: storefront
// ---------------------------------------------------------------------------

/**
 * Resolves a scanned QR value or a restaurant slug.
 *
 * @param client A Supabase client.
 * @param token A `YMT1` table token or a restaurant slug.
 * @returns What the scan pointed at.
 */
export async function resolveScan(client: DbClient, token: string): Promise<ScanResolution> {
  return unwrap(await client.rpc('resolve_scan', { p_token: token })) as unknown as ScanResolution;
}

/**
 * The public storefront profile: identity, policy, and opening hours.
 *
 * @param client A Supabase client.
 * @param slug Restaurant slug.
 * @returns The profile, or null when no such active restaurant exists.
 */
export async function fetchRestaurant(
  client: DbClient,
  slug: string,
): Promise<RestaurantPublic | null> {
  return unwrap(await client.rpc('restaurant_public', { p_slug: slug })) as unknown as RestaurantPublic | null;
}

/**
 * The customer-facing menu with effective prices and sold-out flags resolved.
 *
 * @param client A Supabase client.
 * @param slug Restaurant slug.
 * @returns The menu, or null when the restaurant is unavailable.
 */
export async function fetchMenu(client: DbClient, slug: string): Promise<PublicMenu | null> {
  return unwrap(await client.rpc('public_menu', { p_slug: slug })) as unknown as PublicMenu | null;
}

/**
 * Bookable pickup slots for the next few days.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant id.
 * @param days How many days ahead to enumerate.
 * @returns ISO timestamps, ascending.
 */
export async function fetchAvailableSlots(
  client: DbClient,
  restaurantId: string,
  days = 7,
): Promise<string[]> {
  const rows = unwrap(
    await client.rpc('available_slots', {
      p_restaurant_id: restaurantId,
      p_days: days,
      p_limit: 200,
    }),
  ) as unknown as Array<{ slot: string }>;

  return rows.map((row) => row.slot);
}

// ---------------------------------------------------------------------------
// Customer: ordering
// ---------------------------------------------------------------------------

/**
 * Prices a cart server-side without committing anything.
 *
 * Called on every cart change so the totals, discounts, and ETA the customer
 * sees are the server's, not a local re-implementation.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant id.
 * @param items Cart payload.
 * @param scheduledFor ISO pickup time, or null for as-soon-as-possible.
 * @returns The authoritative quote.
 * @throws ApiError with a customer-readable message when the cart is invalid.
 */
export async function quoteOrder(
  client: DbClient,
  restaurantId: string,
  items: CartItemInput[],
  scheduledFor: string | null = null,
): Promise<OrderQuote> {
  return unwrap(
    await client.rpc('quote_order', {
      p_restaurant_id: restaurantId,
      p_items: asJson(items),
      p_scheduled_for: scheduledFor ?? undefined,
    }),
  ) as unknown as OrderQuote;
}

export interface PlaceOrderInput {
  restaurantId: string;
  items: CartItemInput[];
  customerName: string;
  customerPhone: string;
  customerEmail?: string | null;
  note?: string | null;
  scheduledFor?: string | null;
  tableToken?: string | null;
  source?: 'qr' | 'link' | 'counter';
}

/**
 * Places an order.
 *
 * The server recomputes prices, revalidates availability, reserves inventory,
 * estimates prep time, and queues the receipt — all in one transaction. Any
 * failure rolls the whole thing back.
 *
 * @param client A Supabase client.
 * @param input Order details.
 * @returns The created order.
 */
export async function placeOrder(client: DbClient, input: PlaceOrderInput): Promise<Order> {
  return unwrap(
    await client.rpc('place_order', {
      p_restaurant_id: input.restaurantId,
      p_items: asJson(input.items),
      p_customer_name: input.customerName,
      p_customer_phone: input.customerPhone,
      p_customer_email: input.customerEmail ?? undefined,
      p_note: input.note ?? undefined,
      p_scheduled_for: input.scheduledFor ?? undefined,
      p_table_token: input.tableToken ?? undefined,
      p_source: input.source ?? 'qr',
    }),
  ) as unknown as Order;
}

/**
 * Reads one order. Permitted for its owner and for staff of its restaurant.
 *
 * @param client A Supabase client.
 * @param orderId Order id.
 * @returns The order, or null when it does not exist or is not visible.
 */
export async function fetchOrder(client: DbClient, orderId: string): Promise<Order | null> {
  return unwrap(await client.rpc('get_order', { p_order_id: orderId })) as unknown as Order | null;
}

/**
 * Recovers an order from the short code the customer quotes at the counter.
 *
 * @param client A Supabase client.
 * @param code Six-character order code.
 * @returns The order, or null.
 */
export async function fetchOrderByCode(client: DbClient, code: string): Promise<Order | null> {
  return unwrap(await client.rpc('get_order_by_code', { p_code: code })) as unknown as Order | null;
}

/**
 * The signed-in customer's own orders, newest first.
 *
 * @param client A Supabase client.
 * @param limit Maximum orders to return.
 * @returns Orders belonging to this device's session.
 */
export async function fetchMyOrders(client: DbClient, limit = 30): Promise<Order[]> {
  return unwrap(await client.rpc('my_orders', { p_limit: limit })) as unknown as Order[];
}

/**
 * Cancels the customer's own order. Only permitted while still `pending`.
 *
 * @param client A Supabase client.
 * @param orderId Order id.
 * @param reason Optional free-text reason.
 * @returns The cancelled order.
 */
export async function cancelMyOrder(
  client: DbClient,
  orderId: string,
  reason?: string,
): Promise<Order> {
  return unwrap(
    await client.rpc('cancel_my_order', { p_order_id: orderId, p_reason: reason ?? undefined }),
  ) as unknown as Order;
}

/**
 * Closes the customer's own order at the counter.
 *
 * Only from `ready`: the merchant declares the order ready first, so a customer
 * cannot close one the kitchen has not finished.
 *
 * @param client A Supabase client.
 * @param orderId The order to complete.
 * @returns The completed order.
 */
export async function completeMyOrder(client: DbClient, orderId: string): Promise<Order> {
  return unwrap(
    await client.rpc('complete_my_order', { p_order_id: orderId }),
  ) as unknown as Order;
}

// ---------------------------------------------------------------------------
// Merchant: account and settings
// ---------------------------------------------------------------------------

export interface CreateRestaurantInput {
  name: string;
  slug: string;
  timezone: string;
  currency: string;
  /** Required: the database rejects a restaurant with no collection address. */
  address: string;
  phone?: string;
  description?: string;
  /** ISO 3166-1 alpha-2, kept so an order's origin can be compared. */
  country?: string;
}

/**
 * Creates a restaurant and makes the caller its owner.
 *
 * @param client A Supabase client.
 * @param input Restaurant details.
 * @returns The new restaurant's id, name, slug, timezone, currency, and role.
 */
export async function createRestaurant(
  client: DbClient,
  input: CreateRestaurantInput,
): Promise<{ id: string; name: string; slug: string; timezone: string; currency: string; role: string }> {
  return unwrap(
    await client.rpc('create_restaurant', {
      p_name: input.name,
      p_slug: input.slug,
      p_timezone: input.timezone,
      p_currency: input.currency,
      p_phone: input.phone ?? undefined,
      p_address: input.address,
      p_description: input.description ?? undefined,
      p_country: input.country ?? undefined,
    }),
  ) as unknown as { id: string; name: string; slug: string; timezone: string; currency: string; role: string };
}

/**
 * Every restaurant the signed-in user belongs to.
 *
 * @param client A Supabase client.
 * @returns Restaurant summaries, ordered by name.
 */
export async function fetchMyRestaurants(client: DbClient): Promise<RestaurantSummary[]> {
  return unwrap(await client.rpc('my_restaurants')) as unknown as RestaurantSummary[];
}

/**
 * Full settings for one restaurant, including operational knobs.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant id.
 * @returns The settings row plus hours and exceptions.
 */
export async function fetchMerchantRestaurant(
  client: DbClient,
  restaurantId: string,
): Promise<MerchantRestaurant> {
  return unwrap(
    await client.rpc('merchant_restaurant', { p_restaurant_id: restaurantId }),
  ) as unknown as MerchantRestaurant;
}

// ---------------------------------------------------------------------------
// Merchant: menu
// ---------------------------------------------------------------------------

/**
 * The staff view of the menu: unpublished items, inventory, discounts, and
 * availability windows all included.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant id.
 * @returns Categories and items.
 */
export async function fetchMerchantMenu(
  client: DbClient,
  restaurantId: string,
): Promise<MerchantMenu> {
  return unwrap(
    await client.rpc('restaurant_menu', { p_restaurant_id: restaurantId }),
  ) as unknown as MerchantMenu;
}

/**
 * Printed table QR codes, with freshly minted signed tokens.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant id.
 * @returns Tables with their current tokens.
 */
export async function fetchTables(
  client: DbClient,
  restaurantId: string,
): Promise<RestaurantTable[]> {
  return unwrap(
    await client.rpc('list_restaurant_tables', { p_restaurant_id: restaurantId }),
  ) as unknown as RestaurantTable[];
}

// ---------------------------------------------------------------------------
// Merchant: order flow
// ---------------------------------------------------------------------------

/**
 * Orders still needing kitchen attention, in the order they should be worked.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant id.
 * @returns Active orders.
 */
export async function fetchLiveQueue(client: DbClient, restaurantId: string): Promise<Order[]> {
  return unwrap(
    await client.rpc('restaurant_live_queue', { p_restaurant_id: restaurantId }),
  ) as unknown as Order[];
}

/**
 * One page of the shop's own calendar day of closed orders.
 *
 * `total` is the day's matching count rather than the page's length: the record
 * needs to tell "that is the whole day" from "that is the end of this page", and
 * a count that shrank with the page size would say the day did.
 */
export interface DayOrders {
  /** The page, newest first. */
  orders: Order[];
  /** How many closed orders the day holds, matching the search. */
  total: number;
}

/**
 * The day's closed orders, newest first.
 *
 * The day is the restaurant's own — the same one `restaurant_dashboard` buckets
 * by — so the record and the takings page cannot disagree about which day an
 * order belongs to. `offset` is what makes the day reachable rather than capped:
 * the screen pages through it instead of dropping everything past the first
 * window.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant id.
 * @param options Page size, cursor, and a search over the whole day.
 * @returns The page and the day's matching count.
 */
export async function fetchDayOrders(
  client: DbClient,
  restaurantId: string,
  options: { limit?: number; offset?: number; search?: string } = {},
): Promise<DayOrders> {
  const { limit = 50, offset = 0, search } = options;
  return unwrap(
    await client.rpc('restaurant_day_orders', {
      p_restaurant_id: restaurantId,
      p_limit: limit,
      p_offset: offset,
      // The generated type wants a string, and the function treats blank as
      // "everything" — so an absent search is the empty string rather than a
      // null the RPC would have to special-case twice.
      p_search: search && search.trim() !== '' ? search.trim() : '',
    }),
  ) as unknown as DayOrders;
}

/**
 * Moves an order to a new status.
 *
 * The database validates the transition, re-stamps the relevant timestamp,
 * re-estimates prep time on acceptance, and records an audit event.
 *
 * @param client A Supabase client.
 * @param orderId Order id.
 * @param status Target status.
 * @param reason Optional reason, required in practice for cancellations.
 * @returns The updated order.
 */
export async function setOrderStatus(
  client: DbClient,
  orderId: string,
  status: Order['status'],
  reason?: string,
  code?: CancelCode,
): Promise<Order> {
  return unwrap(
    await client.rpc('merchant_set_order_status', {
      p_order_id: orderId,
      p_status: status,
      p_reason: reason ?? undefined,
      p_code: code ?? undefined,
    }),
  ) as unknown as Order;
}

/**
 * Marks a whole cooking round ready and records it as one batch.
 *
 * All or nothing, by the product's own rule: the cook page has no concept of a
 * partially marked round, and a half-applied batch would tell some customers to
 * collect while the rest of the wok was still being plated. An order somebody has
 * already dealt with refuses the entire call.
 *
 * One request rather than one per ticket, which is what a loop over
 * `setOrderStatus` would need — and the write is where a round stops being a view
 * that vanishes with the screen and becomes a measurable fact. The server stores
 * the batch's membership, its deadline and the inferred moment it started, which
 * is what makes batch-wait separable from waiting for a free burner.
 *
 * @param client A Supabase client.
 * @param orderIds Every order in the round.
 * @param readyBy The round's deadline — the earliest ready-by in it, in epoch milliseconds.
 * @param options `source` says how the round was cut, and `lateAcceptedIds` the
 *   tickets the counter accepted after the board had already called them late.
 * @returns The updated orders.
 */
export async function completeCookRound(
  client: DbClient,
  orderIds: string[],
  readyBy: number,
  options: { source?: 'round' | 'single'; lateAcceptedIds?: string[] } = {},
): Promise<Order[]> {
  return unwrap(
    await client.rpc('merchant_complete_round', {
      p_order_ids: orderIds,
      p_ready_by: new Date(readyBy).toISOString(),
      p_source: options.source ?? 'round',
      p_late_accepted_ids: options.lateAcceptedIds ?? [],
    }),
  ) as unknown as Order[];
}

/**
 * What the kitchen's own record says about its capacity.
 *
 * The waiting/cooking split, the batch sizes, and the arrivals per local hour,
 * over a 90-day window. Everything measured comes from logged batches; the
 * arrival rate comes from every order placed. See `docs/cook-rounds.md`.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant id.
 * @returns The measured split, the arrivals, and the settings to start from.
 */
export async function fetchCookInsights(
  client: DbClient,
  restaurantId: string,
): Promise<CookInsights> {
  return unwrap(
    await client.rpc('restaurant_cook_insights', { p_restaurant_id: restaurantId }),
  ) as unknown as CookInsights;
}

/**
 * The live policy for cutting the next round.
 *
 * The target batch size and the longest wait the shop already tolerates, plus
 * the last hour's arrivals and the load they imply. Inputs rather than a
 * decision: the wait that decides a cutoff grows with the clock, so the page
 * decides with `roundCutoff` and a live clock rather than with a stale answer.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant id.
 * @returns The thresholds and the running context.
 */
export async function fetchCookQueue(client: DbClient, restaurantId: string): Promise<CookQueue> {
  return unwrap(
    await client.rpc('restaurant_cook_queue', { p_restaurant_id: restaurantId }),
  ) as unknown as CookQueue;
}

/**
 * Records or clears the moment the counter took the money.
 *
 * A fact beside the status rather than a stage of it: an order can be paid while the
 * customer is still queueing and cancelled after it is paid, and both directions are
 * reversible because a mis-tapped payment has to be — which a status change is not.
 * Nothing gates on it, so this does not care what state the order is in.
 *
 * @param client A Supabase client.
 * @param orderId Order id.
 * @param paid Whether the money has been taken.
 * @returns The updated order.
 */
export async function setOrderPaid(
  client: DbClient,
  orderId: string,
  paid: boolean,
): Promise<Order> {
  return unwrap(
    await client.rpc('merchant_set_order_paid', { p_order_id: orderId, p_paid: paid }),
  ) as unknown as Order;
}

/**
 * Why a merchant cancelled.
 *
 * `no_show` is the only one that counts against the customer: the food was made
 * and nobody came. The rest are the shop's own reasons, and a history that
 * counted them would blame customers for a sold-out day.
 */
export type CancelCode = 'no_show' | 'sold_out' | 'other';

/** A number a merchant has refused. */
export interface BlockedNumber {
  id: string;
  phone: string;
  reason: string | null;
  note: string | null;
  blocked_at: string;
}

/**
 * The order a pickup token names, for the merchant who owns it.
 *
 * Reads only. The scanned code opens a screen that shows the order and asks for
 * confirmation, rather than completing it by being opened — a GET that
 * completes is a GET a link preview can fire.
 *
 * @param client A Supabase client.
 * @param token The pickup token from the customer's code.
 * @returns The order, or null when the token names nothing.
 */
export async function fetchOrderByPickupToken(
  client: DbClient,
  token: string,
): Promise<Order | null> {
  return unwrap(
    await client.rpc('merchant_order_by_token', { p_token: token }),
  ) as unknown as Order | null;
}

/**
 * Refuses a number at one restaurant.
 *
 * Per restaurant, never global: one shop's prankster is another shop's regular.
 * On the phone rather than the IP, because an address is a crowd.
 */
export async function blockNumber(
  client: DbClient,
  restaurantId: string,
  phone: string,
  reason?: string,
  note?: string,
): Promise<void> {
  unwrap(
    await client.from('restaurant_blocks').insert({
      restaurant_id: restaurantId,
      phone,
      reason: reason ?? null,
      note: note ?? null,
    }),
  );
}

/** Lifts a block. Blocks are made in anger and reconsidered; a mistake must not be permanent. */
export async function unblockNumber(
  client: DbClient,
  restaurantId: string,
  phone: string,
): Promise<void> {
  unwrap(
    await client.from('restaurant_blocks').delete().eq('restaurant_id', restaurantId).eq('phone', phone),
  );
}

/** Every number this restaurant has refused, newest first. */
export async function listBlockedNumbers(
  client: DbClient,
  restaurantId: string,
): Promise<BlockedNumber[]> {
  return unwrap(
    await client
      .from('restaurant_blocks')
      .select('id, phone, reason, note, blocked_at')
      .eq('restaurant_id', restaurantId)
      .order('blocked_at', { ascending: false }),
  ) as unknown as BlockedNumber[];
}

/**
 * Completes an order from a scanned pickup QR.
 *
 * The token is verified server-side, so a screenshot of someone else's code
 * cannot be redeemed.
 *
 * @param client A Supabase client.
 * @param token The `YMP1` token scanned from the customer's phone.
 * @returns The completed order.
 */
export async function completeOrderByToken(client: DbClient, token: string): Promise<Order> {
  return unwrap(
    await client.rpc('complete_order_by_token', { p_token: token }),
  ) as unknown as Order;
}

/**
 * Completes an order from the short code the customer reads out.
 *
 * @param client A Supabase client.
 * @param code Six-character order code.
 * @returns The completed order.
 */
export async function completeOrderByCode(client: DbClient, code: string): Promise<Order> {
  return unwrap(
    await client.rpc('complete_order_by_code', { p_code: code }),
  ) as unknown as Order;
}

// ---------------------------------------------------------------------------
// Merchant: analytics
// ---------------------------------------------------------------------------

/**
 * Aggregated sales and fulfilment statistics for a date range.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant id.
 * @param range A named range resolved in the restaurant's own timezone.
 * @returns Summary, per-day series, hourly histogram, and top items.
 */
export async function fetchDashboard(
  client: DbClient,
  restaurantId: string,
  window: DashboardWindow = { kind: 'preset', range: 'today' },
): Promise<DashboardStats> {
  return unwrap(
    await client.rpc('restaurant_dashboard_range', {
      p_restaurant_id: restaurantId,
      // A preset is named and nothing else is sent; a custom window sends its two
      // days and the server ignores the keyword. One function either way, so the
      // month arithmetic has one home.
      ...(window.kind === 'preset' ? { p_range: window.range } : { p_from: window.from, p_to: window.to }),
    }),
  ) as unknown as DashboardStats;
}

/**
 * Whether customer receipts are actually leaving the building.
 *
 * Worth surfacing in the dashboard: a merchant who believes receipts are being
 * sent, and is wrong, only finds out when a customer asks.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant id.
 * @returns Provider configuration and per-status outbox counts.
 */
export async function fetchEmailStatus(
  client: DbClient,
  restaurantId: string,
): Promise<EmailStatus> {
  return unwrap(
    await client.rpc('restaurant_email_status', { p_restaurant_id: restaurantId }),
  ) as unknown as EmailStatus;
}

/**
 * How much history the prep-time estimator has learned from.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant id.
 * @returns Sample counts and prep-time percentiles.
 */
export async function fetchEtaInsights(
  client: DbClient,
  restaurantId: string,
): Promise<EtaInsights> {
  return unwrap(
    await client.rpc('restaurant_eta_insights', { p_restaurant_id: restaurantId }),
  ) as unknown as EtaInsights;
}
